import { supabase } from './supabaseClient';
import { getAuthHeaders } from './authService';
import { API_BASE_URL } from './config/apiConfig';

// =============================
// Institutes
// =============================

export async function getAllInstitutes() {
  const { data, error } = await supabase
    .from('institutes')
    .select('*')
    .eq('is_active', true)
    .order('name', { ascending: true });
  return { data, error };
}

export async function getInstituteById(instituteId) {
  const { data, error } = await supabase
    .from('institutes')
    .select('*')
    .eq('id', instituteId)
    .eq('is_active', true)
    .single();
  return { data, error };
}

// =============================
// Institute Sessions
// =============================

export async function getInstituteSessions(instituteId) {
  const { data, error } = await supabase
    .from('institute_sessions')
    .select('*')
    .eq('institute_id', instituteId)
    .eq('is_active', true)
    .gte('date', new Date().toISOString().split('T')[0]) // Only future sessions
    .order('date', { ascending: true })
    .order('time_from', { ascending: true });
  return { data, error };
}

export async function createInstituteSession(sessionData) {
  const { data, error } = await supabase
    .from('institute_sessions')
    .insert([sessionData])
    .select()
    .single();
  return { data, error };
}

export async function updateInstituteSession(sessionId, updates) {
  const { data, error } = await supabase
    .from('institute_sessions')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', sessionId)
    .select()
    .single();
  return { data, error };
}

export async function deleteInstituteSession(sessionId) {
  const { data, error } = await supabase
    .from('institute_sessions')
    .update({ is_active: false })
    .eq('id', sessionId)
    .select()
    .single();
  return { data, error };
}

// =============================
// Institute Session Requests
// =============================

export async function createInstituteSessionRequest(requestData) {
  // Ensure session_type and format are included
  const requestPayload = {
    ...requestData,
    session_type: requestData.session_type || 'session',
    format: requestData.format || 'in_person'
  };

  const { data, error } = await supabase
    .from('institute_session_requests')
    .insert([requestPayload])
    .select()
    .single();
  
  // Send notification email if successful
  if (data && !error) {
    try {
      await fetch(`${API_BASE_URL}/api/send-institute-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instituteId: data.institute_id,
          studentEmail: data.student_email,
          studentName: data.student_name,
          requestType: data.request_type,
          sessionType: data.session_type,
          format: data.format,
          title: data.title,
          subject: data.subject,
          preferredDate: data.preferred_date,
          preferredTimeFrom: data.preferred_time_from,
          preferredTimeTo: data.preferred_time_to,
          numStudents: data.num_students,
          message: data.message
        })
      });
    } catch (emailError) {
      console.error('Error sending institute request notification:', emailError);
    }
  }
  
  return { data, error };
}

// Book and pay for an institute session (automatic enrollment)
export async function bookInstituteSession(sessionId, studentData) {
  try {
    // First, check if session has available space
    const { data: session, error: sessionError } = await supabase
      .from('institute_sessions')
      .select('*')
      .eq('id', sessionId)
      .single();

    if (sessionError) throw sessionError;
    if (!session) throw new Error('Session not found');

    // Check if session is full
    if ((session.current_students || 0) >= session.max_students) {
      throw new Error('Session is full');
    }

    // Check if session date hasn't passed
    const sessionDate = new Date(session.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (sessionDate < today) {
      throw new Error('Cannot book past sessions');
    }

    // Create booking record with status 'accepted' and payment_status 'paid'
    const bookingData = {
      institute_id: session.institute_id,
      session_id: sessionId,
      student_email: studentData.email,
      student_name: studentData.name,
      student_phone: studentData.phone || null,
      request_type: 'existing',
      title: session.title,
      subject: session.subject,
      teacher_name: session.teacher_name,
      place: session.place,
      preferred_date: session.date,
      preferred_time_from: session.time_from,
      preferred_time_to: session.time_to,
      num_students: 1,
      student_names: [studentData.name],
      student_emails: [studentData.email],
      status: 'accepted', // Automatically accepted
      payment_status: 'paid', // Payment completed
      payment_amount: session.price || 0,
      payment_date: new Date().toISOString(),
      meeting_link: session.meeting_link || null, // Include meeting link for online sessions
      message: `Booked ${session.session_type || 'session'}: ${session.title}`
    };

    const { data: booking, error: bookingError } = await supabase
      .from('institute_session_requests')
      .insert([bookingData])
      .select()
      .single();

    if (bookingError) throw bookingError;

    // Increment current_students count
    const { error: updateError } = await supabase
      .from('institute_sessions')
      .update({ 
        current_students: (session.current_students || 0) + 1,
        updated_at: new Date().toISOString()
      })
      .eq('id', sessionId);

    if (updateError) throw updateError;

    // Send confirmation email with meeting link if online
    if (booking) {
      try {
        await fetch(`${API_BASE_URL}/api/send-institute-booking-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            studentEmail: studentData.email,
            studentName: studentData.name,
            sessionTitle: session.title,
            sessionType: session.session_type || 'session',
            format: session.format || 'in_person',
            sessionDate: session.date,
            sessionTime: `${session.time_from} - ${session.time_to}`,
            teacherName: session.teacher_name,
            place: session.place,
            meetingLink: session.meeting_link || null,
            price: session.price || 0
          })
        });
      } catch (emailError) {
        console.error('Error sending booking confirmation email:', emailError);
        // Don't throw - booking is successful even if email fails
      }
    }

    return { data: booking, error: null };
  } catch (error) {
    console.error('Error booking institute session:', error);
    return { data: null, error };
  }
}

export async function getStudentInstituteRequests(studentEmail) {
  const { data, error } = await supabase
    .from('institute_session_requests')
    .select(`
      *,
      institutes:institute_id (
        id,
        name,
        logo_url
      ),
      sessions:session_id (
        id,
        title,
        teacher_name
      )
    `)
    .eq('student_email', studentEmail)
    .order('created_at', { ascending: false });
  return { data, error };
}

// =============================
// Institute Analytics
// =============================

export async function getGlobalStudentSchoolStats() {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/api/institute/analytics/schools`, {
    method: 'GET',
    headers
  });

  const data = await response.json();

  if (!response.ok || data.success === false) {
    throw new Error(data.error || 'Failed to load student school analytics');
  }

  return data;
}

export async function getInstituteRequests(instituteId) {
  const { data, error } = await supabase
    .from('institute_session_requests')
    .select(`
      *,
      sessions:session_id (
        id,
        title,
        teacher_name
      )
    `)
    .eq('institute_id', instituteId)
    .order('created_at', { ascending: false });
  return { data, error };
}

// =============================
// Waitlist Functions
// =============================

export async function addToWaitlist(sessionId, instituteId, studentData) {
  try {
    const { data, error } = await supabase
      .from('institute_session_waitlist')
      .insert([{
        session_id: sessionId,
        institute_id: instituteId,
        student_email: studentData.email,
        student_name: studentData.name,
        student_phone: studentData.phone || null,
        status: 'waiting'
      }])
      .select()
      .single();

    return { data, error };
  } catch (error) {
    console.error('Error adding to waitlist:', error);
    return { data: null, error };
  }
}

export async function getWaitlistForSession(sessionId) {
  const { data, error } = await supabase
    .from('institute_session_waitlist')
    .select('*')
    .eq('session_id', sessionId)
    .eq('status', 'waiting')
    .order('created_at', { ascending: true });

  return { data, error };
}

export async function getStudentWaitlist(studentEmail) {
  const { data, error } = await supabase
    .from('institute_session_waitlist')
    .select(`
      *,
      sessions:session_id (
        id,
        title,
        date,
        time_from,
        time_to,
        format,
        meeting_link,
        max_students,
        current_students
      ),
      institutes:institute_id (
        id,
        name,
        logo_url
      )
    `)
    .eq('student_email', studentEmail)
    .order('created_at', { ascending: false });

  return { data, error };
}

// =============================
// Session Templates
// =============================

export async function getSessionTemplates(instituteId) {
  const { data, error } = await supabase
    .from('institute_session_templates')
    .select('*')
    .eq('institute_id', instituteId)
    .order('created_at', { ascending: false });

  return { data, error };
}

export async function createSessionTemplate(instituteId, templateData) {
  const { data, error } = await supabase
    .from('institute_session_templates')
    .insert([{
      institute_id: instituteId,
      ...templateData
    }])
    .select()
    .single();

  return { data, error };
}

export async function updateSessionTemplate(templateId, templateData) {
  const { data, error } = await supabase
    .from('institute_session_templates')
    .update({
      ...templateData,
      updated_at: new Date().toISOString()
    })
    .eq('id', templateId)
    .select()
    .single();

  return { data, error };
}

export async function deleteSessionTemplate(templateId) {
  const { data, error } = await supabase
    .from('institute_session_templates')
    .delete()
    .eq('id', templateId)
    .select()
    .single();

  return { data, error };
}

// =============================
// Waitlist Management
// =============================

export async function getWaitlistForInstitute(instituteId) {
  const { data, error } = await supabase
    .from('institute_session_waitlist')
    .select(`
      *,
      sessions:institute_sessions!session_id (
        id,
        title,
        date,
        time_from,
        time_to,
        format,
        meeting_link,
        max_students,
        current_students
      )
    `)
    .eq('institute_id', instituteId)
    .eq('status', 'waiting')
    .order('created_at', { ascending: true });

  return { data, error };
}

export async function notifyWaitlistStudent(waitlistId) {
  const { data, error } = await supabase
    .from('institute_session_waitlist')
    .update({ 
      status: 'notified',
      notified_at: new Date().toISOString()
    })
    .eq('id', waitlistId)
    .select()
    .single();

  return { data, error };
}

export async function removeFromWaitlist(waitlistId) {
  const { data, error } = await supabase
    .from('institute_session_waitlist')
    .update({ status: 'cancelled' })
    .eq('id', waitlistId)
    .select()
    .single();

  return { data, error };
}

export async function updateInstituteRequestStatus(requestId, status, meetingLink = null) {
  const updates = {
    status,
    updated_at: new Date().toISOString()
  };
  
  if (meetingLink) {
    updates.meeting_link = meetingLink;
  }
  
  const { data, error } = await supabase
    .from('institute_session_requests')
    .update(updates)
    .eq('id', requestId)
    .select()
    .single();
  
  // Send notification email
  if (data && !error) {
    try {
      await fetch(`${API_BASE_URL}/api/send-institute-request-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId: data.id,
          studentEmail: data.student_email,
          studentName: data.student_name,
          status: data.status,
          meetingLink: data.meeting_link
        })
      });
    } catch (emailError) {
      console.error('Error sending request update notification:', emailError);
    }
  }
  
  return { data, error };
}

// =============================
// Institute Users (Admin/Teacher)
// =============================

export async function getInstituteForUser(userEmail) {
  const { data, error } = await supabase
    .from('institute_users')
    .select(`
      *,
      institutes:institute_id (
        *
      )
    `)
    .eq('user_email', userEmail)
    .maybeSingle(); // Use maybeSingle instead of single to avoid error when no rows found
  return { data, error };
}

export async function checkUserIsInstituteAdmin(userEmail, instituteId) {
  const { data, error } = await supabase
    .from('institute_users')
    .select('*')
    .eq('user_email', userEmail)
    .eq('institute_id', instituteId)
    .in('role', ['admin', 'teacher'])
    .single();
  return { data: !!data, error };
}

